import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useMatch, useNavigate, useParams } from "react-router-dom";
import qs from "qs";
import { isSunrunManaged } from "@sunrun/sales-experience-shared";
import { useFlags } from "flagsmith/react";
import { ReviewOffer } from "../../../amplify/backend/function/offerexpstoreFrontApi/ts/public/offerTypes";
import {
  SplatCheckoutStep,
  SplatRole,
  UserProfile,
  ContractReviewMessage,
  SplatCheckoutDocument,
} from "../../../amplify/backend/function/OfferExpCheckout/ts/public/types";
import { SplatProposal } from "../../../amplify/backend/function/OfferExpContacts/ts/public/types";
import { CheckoutTask, useCheckoutStepsData } from "checkout/api/checkoutSteps";
import { mapCheckoutStepsToTasks } from "checkout/util/taskHelpers";
import { useNavigateToCheckout } from "utils/useNavigateHelper";
import {
  useCheckoutAttachmentsNoType,
  useOffer,
  useProspect,
} from "checkout/hooks/useCheckoutSwr";
import { getProposals } from "cap/api/capApi";
import { FeatureFlags } from "fixtures/features";
import { useAuthToken } from "hooks/useAuthToken";
import { useContractReview } from "hooks/useContractReview";
import { getCustomerAppSessionStorageKey } from "checkout/util/constants";
import { applyScheduleSiteSurveyUnlockForHqs } from "checkout/util/hqsCheckoutTaskOverrides";
import { useHqsEligibilityByProposalId } from "hooks/useHqs";
import { isNewHomesChannel } from "utils/newHomes";

interface CheckoutContextType {
  tasks: CheckoutTask[];
  proposalId: string;
  prospectId: string;
  opportunityId: string;
  currentTask: CheckoutTask | undefined;
  currentTaskId: string;
  navigateToNextTask: () => void;
  navigateToTask: (taskToNav: string, queryParams?: object) => void;
  navigateToPrevTask: () => void;
  refreshAndNavigateNext: () => void;
  refreshTasks: () => Promise<any>;
  tasksError: Error | undefined;
  tasksLoading: boolean;
  hasNextTask: boolean;
  isRefetchingTasks: boolean;
  offer?: ReviewOffer;
  isOfferLoading: boolean;
  isNonSolarOffer: boolean;
  isOnlyBattery: boolean;
  isOnlyEvCharger: boolean;
  offerError?: Error;
  financePlan?: string;
  loadingOverride: boolean;
  setLoadingOverride: React.Dispatch<React.SetStateAction<boolean>>;
  titleOverride?: Record<string, string | undefined>;
  handleTitleOverride: (title?: string, taskId?: string) => void;
  isHqsRequired: boolean;
  splatRole: SplatRole;
  showSecondaryWarningBanner: boolean;
  setShowSecondaryWarningBanner: React.Dispatch<React.SetStateAction<boolean>>;
  showUtilityBillWarningBanner: boolean;
  setShowUtilityBillWarningBanner: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  showUtilityBillUploadRequirementBanner: boolean;
  utilityBillPastDueBalanceFlowIsEnabled: boolean;
  hasUploadedUtilityBill: boolean;
  utilityBillAttachments: SplatCheckoutDocument[];
  isUtilityBillAttachmentsLoading: boolean;
  userProfile?: UserProfile;
  selectedProposal?: SplatProposal;
  isSRM?: boolean;
  salesDivision?: string;
  contractReviewMessages: ContractReviewMessage[];
  isContractReviewLoading: boolean;
  isContractReviewInProgress: boolean;
  isNewHomes: boolean;
  hasAdditionalSystem: boolean;
  completeCheckout: boolean;
}

const CheckoutContext = createContext<CheckoutContextType>({
  tasks: [],
  tasksError: undefined,
  proposalId: "",
  prospectId: "",
  opportunityId: "",
  currentTask: undefined,
  currentTaskId: "",
  navigateToNextTask: () => {},
  navigateToPrevTask: () => {},
  navigateToTask: () => {},
  refreshTasks: () => Promise.resolve({}),
  refreshAndNavigateNext: () => {},
  tasksLoading: true,
  hasNextTask: true,
  isRefetchingTasks: false,
  isNonSolarOffer: false,
  isOnlyBattery: false,
  isOnlyEvCharger: false,
  offer: undefined,
  isOfferLoading: false,
  offerError: undefined,
  loadingOverride: false,
  setLoadingOverride: () => {},
  titleOverride: undefined,
  handleTitleOverride: () => {},
  isHqsRequired: false,
  splatRole: "Other",
  showSecondaryWarningBanner: false,
  setShowSecondaryWarningBanner: () => {},
  showUtilityBillWarningBanner: false,
  setShowUtilityBillWarningBanner: () => {},
  contractReviewMessages: [],
  isContractReviewLoading: false,
  isContractReviewInProgress: false,
  showUtilityBillUploadRequirementBanner: false,
  utilityBillPastDueBalanceFlowIsEnabled: false,
  hasUploadedUtilityBill: false,
  utilityBillAttachments: [],
  isUtilityBillAttachmentsLoading: false,
  isNewHomes: false,
  hasAdditionalSystem: false,
  completeCheckout: false,
});

function useCheckoutContext(): CheckoutContextType {
  const context = useContext(CheckoutContext);
  if (!context) {
    throw new Error(
      "useCheckoutContext must be used within a CheckoutContextProvider"
    );
  }
  return context;
}

function insertCustomerAppTask(
  tasks: SplatCheckoutStep[],
  readAndSignDocsOrder: number,
  isAgreementSigned: boolean,
  prospectId: string,
  offerId: string
): SplatCheckoutStep[] {
  const isCompleted =
    sessionStorage.getItem(
      getCustomerAppSessionStorageKey(prospectId, offerId)
    ) === "true";
  const customerAppStep: SplatCheckoutStep = {
    name: "customerApp",
    order: readAndSignDocsOrder + 0.5,
    title: "Customer App",
    odis: [],
    prerequisites: [],
    prerequisiteOdis: [],
    completed: isCompleted,
    isUnlocked: isAgreementSigned,
  };
  return tasks.concat(customerAppStep);
}

const insertConfirmHomeInfoTask = (
  tasks: SplatCheckoutStep[],
  hasUploadedUtilityBill: boolean
): SplatCheckoutStep[] => {
  const confirmHomeInfoStep: SplatCheckoutStep = {
    name: "confirmHomeInfo",
    order: 0,
    title: "Confirm Home Info",
    odis: [],
    prerequisites: [],
    prerequisiteOdis: [],
    completed: Boolean(hasUploadedUtilityBill),
    isUnlocked: true,
  };
  return tasks.concat(confirmHomeInfoStep);
};

/** Include SPLAT's approval step only when Persona is enabled for this user, after Read & Sign. */
const applyApprovalTask = (
  tasks: SplatCheckoutStep[],
  isPersonaVerificationEnabled: boolean
): SplatCheckoutStep[] => {
  const otherSteps = tasks.filter((step) => step.name !== "approval");
  if (!isPersonaVerificationEnabled) {
    return otherSteps;
  }

  const existingApproval = tasks.find((step) => step.name === "approval");
  if (!existingApproval) {
    return otherSteps;
  }
  const readAndSignDocs = otherSteps.find(
    (step) => step.name === "readAndSignDocs"
  );
  const approvalStep: SplatCheckoutStep = {
    ...existingApproval,
    order: readAndSignDocs
      ? readAndSignDocs.order + 0.25
      : existingApproval.order,
  };

  if (!readAndSignDocs) {
    return [...otherSteps, approvalStep];
  }

  const readAndSignIndex = otherSteps.findIndex(
    (step) => step.name === "readAndSignDocs"
  );
  return [
    ...otherSteps.slice(0, readAndSignIndex + 1),
    approvalStep,
    ...otherSteps.slice(readAndSignIndex + 1),
  ];
};

const CheckoutContextProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [showSecondaryWarningBanner, setShowSecondaryWarningBanner] =
    useState(false);
  const [showUtilityBillWarningBanner, setShowUtilityBillWarningBanner] =
    useState(false);
  const [
    showUtilityBillUploadRequirementBanner,
    setShowUtilityBillUploadRequirementBanner,
  ] = useState(false);
  const [loadingOverride, setLoadingOverride] = useState(false);
  const [titleOverride, setTitleOverride] =
    useState<CheckoutContextType["titleOverride"]>();
  const [tasks, setTasks] = useState<CheckoutTask[]>([]);
  const [currentTaskId, setCurrentTaskId] = useState("");
  const nav = useNavigate();
  const match = useMatch(
    "/prospect/:prospectId/offer/:offerId/checkout/:taskSlug"
  );
  const { offerId, prospectId } = useParams();
  const navigateToCheckout = useNavigateToCheckout();

  const baseUrl = `/prospect/${prospectId}/offer/${offerId}/checkout/`;

  const authKey = useAuthToken("HYBRID")?.token ?? "";
  const proposalToken = useAuthToken("PROPOSAL")?.token;
  const cognitoToken = useAuthToken("IHD")?.token;

  const [shouldNavigate, setShouldNavigate] = useState(false);

  const { offer, offerError, isOfferLoading } = useOffer(
    offerId,
    cognitoToken,
    proposalToken
  );
  const { data: prospect } = useProspect(prospectId);
  const proposalId = offer?.proposalId ?? "";

  const flags = useFlags([
    FeatureFlags.UTILITY_PAST_DUE_BALANCE_FLOW_ENABLED,
    FeatureFlags.CONTRACT_REVIEW,
    FeatureFlags.CHECKOUT_SRA,
    FeatureFlags.PERSONA_VERIFICATION,
    FeatureFlags.PERSONA_VERIFICATION_STATES,
  ]);
  const isCustomer = !!proposalToken;
  const isCheckoutSraEnabled =
    flags[FeatureFlags.CHECKOUT_SRA]?.enabled ?? false;
  const isPersonaVerificationEnabled =
    flags[FeatureFlags.PERSONA_VERIFICATION]?.enabled ?? false;
  const personaStateFlag = flags[FeatureFlags.PERSONA_VERIFICATION_STATES];
  const customerState = prospect?.customerState?.trim().toUpperCase();
  const isPersonaStateRolloutEnabled =
    Boolean(customerState) &&
    String(personaStateFlag?.value ?? "")
      .toUpperCase()
      .includes(customerState ?? "");
  const qualifiesForUtilityBillPastDueBalanceFlow =
    offer?.financial?.summary?.type === "FlexMonthly" ||
    offer?.financial?.summary?.type === "Monthly";
  const utilityBillPastDueBalanceFlowIsEnabled =
    (flags[FeatureFlags.UTILITY_PAST_DUE_BALANCE_FLOW_ENABLED]?.enabled &&
      qualifiesForUtilityBillPastDueBalanceFlow) ??
    false;

  const {
    data: checkoutAttachments,
    isLoading: checkoutAttachmentsLoading,
    isValidating: checkoutAttachmentsValidating,
  } = useCheckoutAttachmentsNoType(prospectId ?? "", proposalId);

  const hasUploadedUtilityBill = checkoutAttachments?.some(
    (attachment) => attachment.documentType === "Utility Bill"
  );

  const utilityBillAttachments = useMemo(
    () =>
      (checkoutAttachments ?? [])
        .filter((attachment) => attachment.documentType === "Utility Bill")
        .sort(
          (a, b) =>
            new Date(b.createdDate).getTime() -
            new Date(a.createdDate).getTime()
        ),
    [checkoutAttachments]
  );
  const isUtilityBillAttachmentsLoading = Boolean(
    checkoutAttachmentsLoading || checkoutAttachmentsValidating
  );

  const [selectedProposal, setSelectedProposal] = useState<SplatProposal>();

  useEffect(() => {
    if (!utilityBillPastDueBalanceFlowIsEnabled) {
      setShowUtilityBillUploadRequirementBanner(false);
    } else if (
      currentTaskId === "linkUtilityAccount" ||
      (!checkoutAttachmentsLoading && hasUploadedUtilityBill)
    ) {
      setShowUtilityBillUploadRequirementBanner(false);
    } else {
      setShowUtilityBillUploadRequirementBanner(true);
    }
  }, [
    currentTaskId,
    hasUploadedUtilityBill,
    checkoutAttachments,
    checkoutAttachmentsLoading,
    utilityBillPastDueBalanceFlowIsEnabled,
  ]);

  useEffect(() => {
    if (!prospectId || !proposalId || (!authKey && !proposalToken)) {
      // Should never happen
      console.error(
        `Cannot load selected proposal! prospectId: "${prospectId}" proposalId: "${proposalId}"`
      );
      return;
    }
    getProposals(authKey || proposalToken || "", prospectId, !!proposalToken)
      .then((proposals) => {
        setSelectedProposal(proposals.find((e) => e.proposalId === proposalId));
      })
      .catch((err) => {
        console.error(err);
        return;
      });
  }, [authKey, prospectId, proposalId, proposalToken]);

  const {
    steps,
    stepsError,
    isStepsLoading,
    refetch,
    isRefetchingSteps,
    opportunityId,
    isHqsRequired,
    splatRole,
    userProfile,
    completeCheckout,
  } = useCheckoutStepsData(
    prospectId ?? "",
    proposalId,
    authKey,
    proposalToken
  );

  const { data: hqsEligibilityData } = useHqsEligibilityByProposalId(
    proposalId || undefined
  );

  useEffect(() => {
    if (steps) {
      const isSRM = userProfile && isSunrunManaged(userProfile);
      const isPartner = userProfile?.isPartner;
      const readAndSignDocs = steps.find(
        (task) => task.name === "readAndSignDocs"
      );
      const readAndSignDocsOrder = readAndSignDocs?.order;
      const isReadAndSignDocsCompleted =
        readAndSignDocs?.odis.find((odi) => odi.odiName === "customerAgreement")
          ?.completed ?? false;

      const shouldInsertCustomerAppTask =
        readAndSignDocsOrder && (isSRM || isPartner);
      const shouldInsertConfirmHomeInfoTask =
        isCheckoutSraEnabled && !isCustomer;
      let currentSteps = shouldInsertCustomerAppTask
        ? insertCustomerAppTask(
            steps,
            readAndSignDocsOrder,
            isReadAndSignDocsCompleted,
            prospectId ?? "",
            offerId ?? ""
          )
        : steps;

      if (shouldInsertConfirmHomeInfoTask) {
        currentSteps = insertConfirmHomeInfoTask(
          currentSteps,
          Boolean(hasUploadedUtilityBill)
        );
      }

      const showPersonaApproval =
        Boolean(isSRM) &&
        (isPersonaVerificationEnabled || isPersonaStateRolloutEnabled);

      currentSteps = applyApprovalTask(currentSteps, showPersonaApproval);

      // Convert the "Splat checkout steps" into "Checkout Tasks"
      const rawMappedTaskList = mapCheckoutStepsToTasks(
        currentSteps,
        userProfile?.isPartner,
        utilityBillPastDueBalanceFlowIsEnabled
      );

      const mappedTaskList = applyScheduleSiteSurveyUnlockForHqs(
        rawMappedTaskList,
        Boolean(hqsEligibilityData),
        isReadAndSignDocsCompleted
      );

      if (mappedTaskList.length <= 0) {
        throw new Error("CheckoutContext received no steps.");
      }

      // Any time we get a new list of tasks back from the api we need to confirm that the current
      // task is still a valid selection.
      let foundTask: CheckoutTask | undefined;

      // We always get the task from the url slug if it exists
      foundTask = mappedTaskList.find(
        (task) => task.urlSlug === match?.params.taskSlug
      );

      if (!foundTask) {
        if (currentTaskId !== mappedTaskList[0].id) {
          setCurrentTaskId(mappedTaskList[0].id);
        }
      } else {
        if (currentTaskId !== foundTask.id) {
          setCurrentTaskId(foundTask.id);
        }
      }

      setTasks(mappedTaskList);
    }
  }, [
    currentTaskId,
    match?.params.taskSlug,
    steps,
    userProfile,
    prospectId,
    offerId,
    utilityBillPastDueBalanceFlowIsEnabled,
    hqsEligibilityData,
    isCheckoutSraEnabled,
    isPersonaVerificationEnabled,
    isPersonaStateRolloutEnabled,
    isCustomer,
    hasUploadedUtilityBill,
  ]);

  const currentTask = useMemo(() => {
    return tasks.find((task) => task.id === currentTaskId);
  }, [currentTaskId, tasks]);

  const navigateToTask = useCallback(
    (taskToNav: string, queryParams?: object) => {
      const foundTask = tasks.find((task) => task.id === taskToNav);
      if (!foundTask) {
        console.warn(`Task ${taskToNav} not found, cannot navigate.`);
        return;
      }
      if (!foundTask.isUnlocked) {
        console.warn(`Task ${taskToNav} is locked, cannot navigate.`);
        return;
      }
      if (!match?.params.offerId || !foundTask.urlSlug) {
        console.warn("Cannot nav, missing offer or nav slug");
        return;
      }
      nav(
        `${baseUrl}${foundTask.urlSlug}${
          queryParams ? `?${qs.stringify(queryParams)}` : ""
        }`
      );
      navigateToCheckout(foundTask.urlSlug, match?.params.offerId);
      setCurrentTaskId(foundTask.id);
    },
    [baseUrl, match?.params.offerId, nav, navigateToCheckout, tasks]
  );

  const refreshAndNavigate = useCallback(async () => {
    await refetch();
    setShouldNavigate(true);
  }, [refetch]);

  const currTaskIndex = tasks.findIndex((task) => task.id === currentTaskId);
  const hasNextTask = currTaskIndex !== tasks.length - 1;

  const navigateNext = useCallback(() => {
    const currTaskIndex = tasks.findIndex((task) => task.id === currentTaskId);
    if (currTaskIndex === tasks.length - 1) {
      console.warn("Cannot navigate to next task, on last task");
      return;
    }
    const nextTask = tasks[currTaskIndex + 1].id;
    navigateToTask(nextTask);
  }, [currentTaskId, navigateToTask, tasks]);

  const handleTitleOverride = useCallback(
    (title: string, stepId?: string) => {
      setTitleOverride((prev) => ({
        ...prev,
        [stepId || currentTaskId]: title,
      }));
    },
    [currentTaskId]
  );

  useEffect(() => {
    if (shouldNavigate) {
      setShouldNavigate(false);
      navigateNext();
    }
  }, [shouldNavigate, nav, navigateNext]);

  const isContractReviewEnabled =
    flags[FeatureFlags.CONTRACT_REVIEW]?.enabled ?? false;
  const {
    messages: contractReviewMessages,
    isLoading: isContractReviewLoading,
    isReviewInProgress: isContractReviewInProgress,
  } = useContractReview(
    isContractReviewEnabled ? proposalId || undefined : undefined
  );

  const isSRM = userProfile && isSunrunManaged(userProfile);
  const salesDivision = userProfile && userProfile?.pricingDivision;

  const isNewHomes = isNewHomesChannel(offer?.channel);
  const hasAdditionalSystem = prospect?.additionalSystem ?? false;

  const checkoutContextValue = useMemo(() => {
    return {
      tasks,
      tasksError: stepsError,
      currentTask,
      currentTaskId: currentTaskId,
      tasksLoading: isStepsLoading,
      isRefetchingTasks: isRefetchingSteps,
      isHqsRequired,
      selectedProposal,
      splatRole,
      refreshTasks: refetch,
      hasNextTask: hasNextTask,
      navigateToNextTask: navigateNext,
      navigateToPrevTask: () => {
        const currTaskIndex = tasks.findIndex(
          (task) => task.id === currentTaskId
        );
        if (currTaskIndex === 0) {
          console.warn("Cannot navigate to prev task, on first task");
          return;
        }
        const nextTask = tasks[currTaskIndex - 1].id;
        navigateToTask(nextTask);
      },
      navigateToTask: navigateToTask,
      proposalId,
      prospectId,
      opportunityId,
      offer,
      isOfferLoading,
      isNonSolarOffer: offer?.isNonSolar,
      isOnlyBattery: offer?.isOnlyBattery,
      isOnlyEvCharger: offer?.isOnlyEvCharger,
      offerError,
      financePlan: offer?.financial.financePlan,
      refreshAndNavigateNext: refreshAndNavigate,
      loadingOverride,
      setLoadingOverride,
      titleOverride,
      handleTitleOverride,
      showSecondaryWarningBanner,
      setShowSecondaryWarningBanner,
      showUtilityBillWarningBanner,
      setShowUtilityBillWarningBanner,
      showUtilityBillUploadRequirementBanner,
      hasUploadedUtilityBill,
      utilityBillAttachments,
      isUtilityBillAttachmentsLoading,
      utilityBillPastDueBalanceFlowIsEnabled,
      userProfile,
      isSRM,
      salesDivision,
      contractReviewMessages,
      isContractReviewLoading,
      isContractReviewInProgress,
      isNewHomes,
      hasAdditionalSystem,
      completeCheckout: completeCheckout === true,
    } as CheckoutContextType;
  }, [
    tasks,
    stepsError,
    currentTask,
    currentTaskId,
    isStepsLoading,
    isRefetchingSteps,
    isHqsRequired,
    selectedProposal,
    splatRole,
    refetch,
    hasNextTask,
    navigateNext,
    navigateToTask,
    proposalId,
    prospectId,
    opportunityId,
    offer,
    isOfferLoading,
    offerError,
    refreshAndNavigate,
    loadingOverride,
    titleOverride,
    handleTitleOverride,
    showSecondaryWarningBanner,
    showUtilityBillWarningBanner,
    showUtilityBillUploadRequirementBanner,
    hasUploadedUtilityBill,
    utilityBillAttachments,
    isUtilityBillAttachmentsLoading,
    utilityBillPastDueBalanceFlowIsEnabled,
    userProfile,
    isSRM,
    salesDivision,
    contractReviewMessages,
    isContractReviewLoading,
    isContractReviewInProgress,
    isNewHomes,
    hasAdditionalSystem,
    completeCheckout,
  ]);

  return (
    <CheckoutContext.Provider value={checkoutContextValue}>
      {children}
    </CheckoutContext.Provider>
  );
};

export { useCheckoutContext, CheckoutContextProvider };
export type { CheckoutContextType };
